<script setup lang="ts">
  import { computed, type ComponentPublicInstance } from 'vue';
  import { IconImage, IconRadio, Textarea } from '@/shared/ui';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import LinkIcon from '@/shared/assets/icons/LinkIcon.svg';
  import UploadIcon from '@/shared/assets/icons/UploadIcon.svg';
  import { ATTACHMENT_ACCEPT, MAX_DOCUMENT_SEND_ATTACHMENTS } from '@/shared/config';

  import {
    ALLOWED_EXTENSIONS_LABEL,
    type ElectronicSendMode,
    type LocalUrl,
    useElectronicAttachments,
  } from '../model/electronic-attachments';
  import DocumentSendElectronicUploadSection from './DocumentSendElectronicUploadSection.vue';
  import DocumentSendElectronicUrlSection from './DocumentSendElectronicUrlSection.vue';

  const props = withDefaults(
    defineProps<{
      selectedElectronicMode: ElectronicSendMode;
      message: string;
      messageError?: string;
      gdAttachmentCount: number;
      gdDocumentsTotalBytes: number;
      localFiles: File[];
      localUrls: LocalUrl[];
      urlErrors: string[];
    }>(),
    {
      messageError: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'update:selectedElectronicMode', value: ElectronicSendMode): void;
    (e: 'update:message', value: string): void;
    (e: 'update:localFiles', value: File[]): void;
    (e: 'update:localUrls', value: LocalUrl[]): void;
    (e: 'update:urlErrors', value: string[]): void;
  }>();

  const {
    addUrlRow,
    blurUrl,
    electronicMessage,
    electronicMode,
    fileInput,
    isDragging,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onFilesDropped,
    onFilesSelected,
    openFileDialog,
    removeFile,
    removeUrl,
    sizeError,
    totalAttachments,
    isAtAttachmentLimit,
    tooManyAttachmentsMessage,
    updateUrl,
  } = useElectronicAttachments(props, emit);

  const selectedElectronicModeIcon = computed(() =>
    props.selectedElectronicMode === 'url' ? LinkIcon : UploadIcon
  );

  const selectedElectronicModeLabel = computed(() =>
    props.selectedElectronicMode === 'url' ? 'URL' : 'Upload'
  );

  const setFileInputRef = (element: Element | ComponentPublicInstance | null) => {
    fileInput.value = element as HTMLInputElement | null;
  };
</script>

<template>
  <section class="flex flex-col gap-4 p-6" data-testid="electronic-copy-section">
    <div class="flex flex-col gap-1">
      <h2 class="text-sm font-semibold text-gray-07">Cópia Eletrônica:</h2>
      <p class="text-xs text-gray-03">
        Utilize se deseja anexar mais arquivos junto ao documento que será enviado
      </p>
    </div>

    <div
      class="flex flex-col gap-4 rounded-xl border border-gray-03 bg-gray-14 p-5 text-gray-07"
      data-testid="electronic-requirements-card"
    >
      <div class="flex flex-col gap-3">
        <div class="flex items-center gap-2 text-gray-03">
          <IconImage :src="InfoIcon" alt="Informações" />
          <h3 class="text-sm font-semibold">Requisitos para enviar arquivos</h3>
        </div>

        <p class="text-sm text-gray-07">
          Você pode enviar arquivos através de <span class="font-semibold">Upload</span> ou de
          <span class="font-semibold">URL</span>, para isso, deve selecionar previamente o modo que
          deseja utilizar.
        </p>

        <ul class="flex flex-row list-disc gap-x-6 gap-y-2 pl-5 text-sm">
          <li>O tamanho total do email pode ter até <span class="font-semibold">10 MB.</span></li>
          <li>Podem ser enviados até {{ MAX_DOCUMENT_SEND_ATTACHMENTS }} anexos por vez.</li>
        </ul>
      </div>

      <div class="rounded-xl border border-gray-02 p-5">
        <div class="mb-3 flex items-center gap-2">
          <IconImage
            class="text-gray-03"
            :src="selectedElectronicModeIcon"
            :alt="selectedElectronicModeLabel"
          />
          <span class="text-sm font-semibold text-gray-07">{{ selectedElectronicModeLabel }}</span>
        </div>

        <div class="flex flex-col gap-2 text-sm">
          <template v-if="selectedElectronicMode === 'url'">
            <p>Cole a URL completa do documento, uma por linha.</p>
            <p>
              O nome do anexo é derivado automaticamente da própria URL e pode ser conferido na tela
              de detalhes do envio.
            </p>
          </template>
          <template v-else>
            <p>Arquivos podem ter as extensões:</p>
            <p class="font-medium text-gray-07">
              {{ ALLOWED_EXTENSIONS_LABEL }}
            </p>
          </template>
        </div>
      </div>
    </div>
  </section>

  <section class="flex flex-col gap-4 p-6" data-testid="electronic-mode-selector-section">
    <span class="text-sm font-semibold text-gray-07">Escolha entre os modos de envio:</span>
    <div class="flex flex-wrap gap-6">
      <IconRadio
        v-model="electronicMode"
        variant="small"
        value="upload"
        label="Upload"
        name="electronic-send-mode"
      />
      <IconRadio
        v-model="electronicMode"
        variant="small"
        value="url"
        label="URL"
        name="electronic-send-mode"
      />
    </div>

    <section
      v-if="selectedElectronicMode === 'upload'"
      class="flex flex-col gap-4"
      data-testid="electronic-upload-section"
    >
      <DocumentSendElectronicUploadSection
        :accept="ATTACHMENT_ACCEPT"
        :is-dragging="isDragging"
        :local-files="localFiles"
        :size-error="sizeError"
        :is-at-attachment-limit="isAtAttachmentLimit"
        :set-file-input-ref="setFileInputRef"
        :on-drag-enter="onDragEnter"
        :on-drag-leave="onDragLeave"
        :on-drag-over="onDragOver"
        :on-files-dropped="onFilesDropped"
        :on-files-selected="onFilesSelected"
        :open-file-dialog="openFileDialog"
        :remove-file="removeFile"
      />
    </section>

    <DocumentSendElectronicUrlSection
      v-if="selectedElectronicMode === 'url'"
      :local-urls="localUrls"
      :max-attachments="MAX_DOCUMENT_SEND_ATTACHMENTS"
      :total-attachments="totalAttachments"
      :url-errors="urlErrors"
      :limit-message="tooManyAttachmentsMessage"
      @add-url="addUrlRow"
      @remove-url="removeUrl"
      @update-url="updateUrl"
      @blur-url="blurUrl"
    />
  </section>

  <section class="flex flex-col gap-4 p-6" data-testid="send-message-section">
    <span class="text-sm font-semibold text-gray-07">Mensagem:</span>
    <Textarea
      v-model="electronicMessage"
      rows="5"
      class="min-h-28 resize-none border-gray-02 bg-white-01 px-4 py-3 text-sm text-gray-07 shadow-none"
      data-testid="send-message-textarea"
    />
    <p v-if="messageError" class="text-xs text-red-16" data-testid="send-message-error">
      {{ messageError }}
    </p>
  </section>
</template>
